import { useEffect, useMemo } from 'react';
import { useField } from 'formik';
import moment from 'moment';
import { Divider, Spin, Typography } from 'antd';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import { BrokerUploadSteps } from '../types';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import TruckIconOutlined from 'components/icons/TruckIconOutlined';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import useCodelists from 'hooks/useCodelists';
import useRequest from 'hooks/useRequest';
import useDebounce from 'utils/useDebounce';
import { Cell, GridRow, Header, OptionRows, TopRow } from 'components/new-ui/composed/selects/styles';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { getContainerStatusLabel } from 'utils/containerStatus';
import { useBrokerUploadContext } from '../BrokerUploadProvider';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import { Manifest } from 'store/manifests/types';
import { listManifests } from 'store/manifests/client';
import ManifestSummary from '../components/ManifestSummary';
import { ManifestDetailsCard } from '../components/styles';
import Button from 'components/new-ui/base/button/Button';
import NoJobsInManifestWarning from '../components/NoJobsInManifestWarning';

const stepKey = BrokerUploadSteps.MANIFEST;
const fieldName = 'manifest.manifestId';
const COLUMNS = '1fr 1fr 1fr 1fr';
const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

const formatDateTime = (date: string | null | undefined) => {
    const momentDate = moment(date);
    return date && momentDate.isValid() ? momentDate.format(DATE_TIME_FORMAT) : '-';
};

const getRoute = (manifest: Manifest | undefined) =>
    [manifest?.departurePort, manifest?.declarationsData?.arrivalPort].filter(Boolean).join(' - ') || '-';

const MetaItem = ({ label, value }: { label: string; value: string | number }) => (
    <span>
        <Typography.Text type="secondary">{label}: </Typography.Text>
        <Typography.Text strong>{value}</Typography.Text>
    </span>
);

const ManifestStep = () => {
    const [fieldProps, fieldMeta, fieldHelper] = useField(fieldName);
    const { country, type } = useBrokerUploadContext();
    const { isActive, isComplete, isIdle, resetStep, currentStepConfig } = useMultiStepFormContext<any>();
    const { data: manifestsData, isLoading, doRequest: fetchManifests } = useRequest(listManifests);
    const { selectedManifest, setSelectedManifest } = useBrokerUploadContext();
    const { getIrelandCodelists } = useCodelists();

    const isArrivalNotification =
        country === DeclarationCountry.EU && type === DeclarationInternalType.ARRIVAL_NOTIFICATION_ICS2;

    /**
     * An arrival notification can only be raised once customs has accepted every master ENS of the
     * manifest, so for one only the manifests the service reports as ready are offered.
     */
    const baseParams = useMemo(
        () => (isArrivalNotification ? { size: 50, arrivalEligible: true } : { size: 50 }),
        [isArrivalNotification]
    );

    useEffect(() => {
        fetchManifests(baseParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseParams]);

    // Provides the arrival port names that `ManifestSummary` resolves the route with.
    useEffect(() => {
        getIrelandCodelists(DeclarationTypes.AIS);
    }, [getIrelandCodelists]);

    const debouncedSearch = useDebounce((query: string) => {
        fetchManifests(query?.trim() ? { ...baseParams, query } : baseParams);
    }, 500);

    const toOption = (m: Manifest) => ({
        id: m.id,
        value: m.referenceId,
        pbn: m.pbn,
        gmr: m.gmr,
        ensReference: m.ensReference,
        containerStatus: getContainerStatusLabel(m.declarationsData?.containerIndicator),
        route: getRoute(m),
        departure: formatDateTime(m.declarationsData?.dateTimeOfDeparture),
        jobs: m.jobs?.length ?? 0,
        disabled: (m.jobs?.length ?? 0) === 0,
    });

    const manifestOptions = useMemo(() => {
        const options = (manifestsData?.list ?? []).map(toOption);

        if (selectedManifest && !options.find((o) => o.id === selectedManifest.id)) {
            options.unshift(toOption(selectedManifest));
        }

        return options;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manifestsData?.list, selectedManifest]);

    const disabled = currentStepConfig?.disabled ?? false;
    const sectionTitle = isArrivalNotification ? 'Select Manifest' : 'Manifest';

    if (isIdle(stepKey)) {
        return <FormSectionHeader title={sectionTitle} icon={<TruckIconOutlined />} />;
    }

    if (isComplete(stepKey)) {
        const readonlyManifest = (
            <ReadonlyFormItem
                disabled={disabled}
                label={isArrivalNotification ? 'Manifest ID :' : undefined}
                value={selectedManifest?.referenceId}
                onReset={() => {
                    setSelectedManifest(undefined);
                    resetStep(stepKey, 'manifestId');
                }}
            />
        );

        if (isArrivalNotification) {
            return (
                <div>
                    <FormSectionHeader title={sectionTitle} icon={<TruckIconOutlined />} />
                    <div style={{ marginTop: 16 }}>{readonlyManifest}</div>
                </div>
            );
        }

        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FormSectionHeader title={sectionTitle} icon={<TruckIconOutlined />} />
                    <ReadonlyFormItem
                        disabled={disabled}
                        value={selectedManifest?.referenceId}
                        onReset={() => {
                            setSelectedManifest(undefined);
                            resetStep(stepKey, 'manifestId');
                        }}
                    />
                </div>
                {selectedManifest && (
                    <ManifestDetailsCard>
                        <ManifestSummary manifest={selectedManifest} />
                        <Divider style={{ marginBlock: 10 }} />
                        {(selectedManifest?.jobs?.length ?? 0) < 1 && <NoJobsInManifestWarning />}
                        <Button
                            type="link"
                            style={{ color: 'var(--brand-6)' }}
                            onClick={() => window.open(`/manifests/${selectedManifest.id}`)}
                        >
                            Go to Manifest
                        </Button>
                    </ManifestDetailsCard>
                )}
            </div>
        );
    }

    if (isActive(stepKey)) {
        return (
            <div>
                <FormSectionHeader title={sectionTitle} icon={<TruckIconOutlined />} subtitle="Select Manifest" />

                <FormSelect
                    style={{ marginTop: 16, maxWidth: '100%' }}
                    hideKeys
                    disabled={disabled}
                    placeholder="Search Manifests"
                    selectOptions={manifestOptions}
                    fieldProps={fieldProps}
                    fieldMeta={fieldMeta}
                    fieldHelper={fieldHelper}
                    value={selectedManifest?.referenceId}
                    loading={isLoading}
                    filterOption={false}
                    dropdownMatchSelectWidth={false}
                    onSearch={debouncedSearch}
                    onChange={(manifestId: string) => {
                        const manifest = (manifestsData?.list ?? []).find((m: Manifest) => m.id === manifestId);
                        if (manifest) setSelectedManifest(manifest);
                    }}
                    dropdownRender={(menu) => (
                        <Spin spinning={isLoading}>
                            {!isArrivalNotification && (
                                <Header>
                                    <GridRow style={{ gridTemplateColumns: COLUMNS }}>
                                        <Cell>Manifest ID</Cell>
                                        <Cell>PBN</Cell>
                                        <Cell>GMR</Cell>
                                        <Cell>ENS</Cell>
                                    </GridRow>
                                </Header>
                            )}
                            {isArrivalNotification && !isLoading && !manifestOptions.length ? (
                                <Typography.Text type="secondary" style={{ display: 'block', padding: '8px 12px' }}>
                                    No manifest has all its ENS filings accepted yet.
                                </Typography.Text>
                            ) : (
                                menu
                            )}
                        </Spin>
                    )}
                    renderOptionContent={(item: any) =>
                        isArrivalNotification ? (
                            <OptionRows>
                                <TopRow style={{ gridTemplateColumns: 'minmax(120px, max-content) 1fr' }}>
                                    <Cell>
                                        <Typography>{item.value || '-'}</Typography>
                                    </Cell>
                                    <Cell>
                                        <Typography.Text type="secondary">
                                            {item.containerStatus ? `\u2022 ${item.containerStatus}` : ''}
                                        </Typography.Text>
                                    </Cell>
                                </TopRow>
                                <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr 1fr', gap: 6 }}>
                                    <MetaItem label="Route" value={item.route} />
                                    <MetaItem label="Departure" value={item.departure} />
                                    <MetaItem label="Jobs" value={item.jobs} />
                                </div>
                            </OptionRows>
                        ) : (
                            <GridRow style={{ gridTemplateColumns: COLUMNS }}>
                                <Cell>
                                    <Typography>{item.value || '-'}</Typography>
                                </Cell>
                                <Cell>
                                    <Typography>{item.pbn || '-'}</Typography>
                                </Cell>
                                <Cell>
                                    <Typography>{item.gmr || '-'}</Typography>
                                </Cell>
                                <Cell>
                                    <Typography>{item.ensReference || '-'}</Typography>
                                </Cell>
                            </GridRow>
                        )
                    }
                />
            </div>
        );
    }

    return <FormSectionHeader title={sectionTitle} icon={<TruckIconOutlined />} />;
};

export default ManifestStep;
